/**
 * Clipboard helpers for TipTap editor.
 * Handles paste from Google Docs (cleaning markup, re-uploading images to S3)
 * and HTML file import (stripping document wrappers, preserving content).
 */

/**
 * Wraps a DOM node's children inside a new tag element.
 * Returns the original node (span stays, new tag is inside it).
 */
function wrapInTag(doc, node, tagName) {
  const tag = doc.createElement(tagName);
  while (node.firstChild) {
    tag.appendChild(node.firstChild);
  }
  node.appendChild(tag);
  return node;
}

/**
 * Unwraps an element, replacing it with its children in the DOM.
 */
function unwrapElement(el) {
  const { parentNode } = el;
  if (!parentNode) return;
  while (el.firstChild) {
    parentNode.insertBefore(el.firstChild, el);
  }
  parentNode.removeChild(el);
}

/**
 * Detects whether pasted HTML originates from Google Docs
 * by checking for the internal GUID marker that Google always injects.
 */
export function isGoogleDocsContent(html) {
  return /docs-internal-guid/.test(html);
}

/**
 * Cleans Google Docs clipboard HTML into editor-friendly markup.
 *
 * Google Docs wraps everything in <b style="font-weight:normal" id="docs-internal-guid-...">
 * and uses <span style="..."> for all formatting instead of semantic tags.
 * This function converts that back to clean HTML that TipTap's existing
 * extensions (FontSize, FontFamily, Color, TextAlign, etc.) can parse.
 */
export function cleanGoogleDocsHtml(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Remove the outer <b id="docs-internal-guid-..."> wrapper
  const guids = doc.querySelectorAll('b[id^="docs-internal-guid"]');
  guids.forEach((wrapper) => {
    const { parentNode } = wrapper;
    while (wrapper.firstChild) {
      parentNode.insertBefore(wrapper.firstChild, wrapper);
    }
    parentNode.removeChild(wrapper);
  });

  // Convert Google's span-based formatting to semantic HTML tags
  // while preserving visual styles (font-size, color, etc.) for TipTap extensions
  const spans = doc.querySelectorAll('span[style]');
  spans.forEach((span) => {
    const { style } = span;

    // font-weight:700 or bold → <strong>
    const fw = style.fontWeight;
    if (fw === '700' || fw === 'bold') {
      wrapInTag(doc, span, 'strong');
      style.removeProperty('font-weight');
    }

    // font-style:italic → <em>
    if (style.fontStyle === 'italic') {
      wrapInTag(doc, span, 'em');
      style.removeProperty('font-style');
    }

    // text-decoration:underline → <u>
    const td = style.textDecoration;
    if (td && td.includes('underline')) {
      wrapInTag(doc, span, 'u');
      style.removeProperty('text-decoration');
    }

    // text-decoration:line-through → <s>
    if (td && td.includes('line-through')) {
      wrapInTag(doc, span, 's');
      style.removeProperty('text-decoration');
    }

    // Remove Google-specific noise styles that don't affect rendering
    style.removeProperty('font-variant');
    style.removeProperty('vertical-align');
    style.removeProperty('white-space');
    style.removeProperty('-webkit-text-decoration-skip');
    style.removeProperty('text-decoration-skip-ink');

    // Remove transparent background (Google Docs default)
    if (style.backgroundColor === 'transparent') {
      style.removeProperty('background-color');
    }

    // Remove default black color to keep HTML clean
    if (style.color === 'rgb(0, 0, 0)' || style.color === '#000000') {
      style.removeProperty('color');
    }

    // If span has no remaining styles, unwrap it
    if (!span.getAttribute('style') || span.style.length === 0) {
      unwrapElement(span);
    }
  });

  // Clean up paragraph dir="ltr" attribute (Google Docs always adds it)
  doc.querySelectorAll('[dir="ltr"]').forEach((el) => {
    el.removeAttribute('dir');
  });

  // Remove empty paragraphs that Google adds as spacers
  // (but keep <br> paragraphs since they're intentional line breaks)
  doc.querySelectorAll('p').forEach((p) => {
    if (!p.textContent.trim() && !p.querySelector('img, br')) {
      p.remove();
    }
  });

  // Convert Google Docs margin-top/bottom to a simpler margin
  doc.querySelectorAll('p[style], h1[style], h2[style], h3[style], h4[style], h5[style], h6[style]').forEach((el) => {
    const { style } = el;

    // Remove 0pt margins (Google Docs default, just noise)
    if (style.marginTop === '0pt') style.removeProperty('margin-top');
    if (style.marginBottom === '0pt') style.removeProperty('margin-bottom');

    // If no style left, remove the attribute entirely
    if (!el.getAttribute('style') || el.style.length === 0) {
      el.removeAttribute('style');
    }
  });

  return doc.body.innerHTML;
}

/**
 * Extracts image blobs from clipboard items and uploads them to S3,
 * then replaces temporary Google image URLs in the HTML with permanent S3 URLs.
 *
 * Google Docs images in clipboard HTML have temporary lh7-*.googleusercontent.com URLs
 * that expire. The browser also places the actual image data as blob items in
 * clipboardData.items, in the same order as the <img> tags in the HTML.
 */
export async function extractAndUploadImages(html, clipboardItems, imageUploadHandler) {
  if (!imageUploadHandler) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const imgs = doc.querySelectorAll('img');

  if (imgs.length === 0) return html;

  // Collect image blobs from clipboard items (they match img tags by order)
  const imageBlobs = [];
  if (clipboardItems) {
    for (let i = 0; i < clipboardItems.length; i += 1) {
      const item = clipboardItems[i];
      if (item.type && item.type.startsWith('image/')) {
        const blob = item.getAsFile ? item.getAsFile() : null;
        if (blob) imageBlobs.push(blob);
      }
    }
  }

  // Upload each image and replace URLs
  const uploadPromises = Array.from(imgs).map(async (img, index) => {
    const src = img.getAttribute('src');

    // Skip images already hosted on our S3 or CDN
    if (src && (src.includes('foreverstudios.com') || src.includes('memoya-production.s3'))) {
      return;
    }

    let blob = imageBlobs[index] || null;

    // Fallback: try fetching the image directly if no clipboard blob
    if (!blob && src && !src.startsWith('data:')) {
      try {
        const response = await fetch(src);
        blob = await response.blob();
      } catch (err) {
        // CORS or network error — keep original URL
        console.warn('[clipboard-helpers] Could not fetch image:', src, err);
        return;
      }
    }

    // Upload base64 images too
    if (!blob && src && src.startsWith('data:')) {
      try {
        const response = await fetch(src);
        blob = await response.blob();
      } catch (err) {
        console.warn('[clipboard-helpers] Could not convert base64 image:', err);
        return;
      }
    }

    if (!blob) return;

    try {
      const ext = blob.type === 'image/png' ? '.png' : '.jpg';
      const fileName = `pasted-image-${Date.now()}-${index}${ext}`;
      const blobFn = () => blob;
      const filenameFn = () => fileName;

      const uploadedUrl = await new Promise((resolve) => {
        imageUploadHandler({ blob: blobFn, filename: filenameFn }, (url) => {
          resolve(url);
        });
      });

      if (uploadedUrl) {
        img.setAttribute('src', uploadedUrl);
      }
    } catch (err) {
      console.warn('[clipboard-helpers] Image upload failed:', err);
    }
  });

  await Promise.all(uploadPromises);

  return doc.body.innerHTML;
}

/**
 * Checks whether an HTML string contains any base64-encoded images.
 */
export function hasBase64Images(html) {
  return /src=["']data:image\//i.test(html);
}

/**
 * Finds all base64 <img> sources in the HTML, uploads each to S3
 * via imageUploadHandler, and replaces src with the returned URL.
 *
 * Used for any paste or import path — not just Google Docs.
 * Each upload is independent so one failure won't block others.
 */
export async function replaceBase64Images(html, imageUploadHandler) {
  if (!imageUploadHandler) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const imgs = doc.querySelectorAll('img');

  const uploadPromises = Array.from(imgs).map(async (img, index) => {
    const src = img.getAttribute('src');
    if (!src || !src.startsWith('data:image/')) return;

    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const ext = blob.type === 'image/png' ? '.png' : '.jpg';
      const fileName = `imported-image-${Date.now()}-${index}${ext}`;

      const uploadedUrl = await new Promise((resolve) => {
        imageUploadHandler(
          { blob: () => blob, filename: () => fileName },
          (url) => resolve(url),
        );
      });

      if (uploadedUrl) {
        img.setAttribute('src', uploadedUrl);
      }
    } catch (err) {
      console.warn('[clipboard-helpers] Base64 image upload failed:', err);
    }
  });

  await Promise.all(uploadPromises);
  return doc.body.innerHTML;
}

/**
 * Cleans imported HTML file content for editor insertion.
 * Strips document-level wrappers (<html>, <head>, <body>),
 * removes scripts and style blocks, preserves all inline styles and content.
 */
export function cleanImportedHtml(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  // Remove elements that should never be in editor content
  doc.querySelectorAll('script, style, meta, link, title, noscript').forEach((el) => {
    el.remove();
  });

  // Remove HTML comments
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_COMMENT);
  const comments = [];
  while (walker.nextNode()) {
    comments.push(walker.currentNode);
  }
  comments.forEach((comment) => comment.remove());

  return doc.body.innerHTML;
}
